'use client';

import React, { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Metric {
  label: string;
  value: string;
  improvement: string;
}

interface TimelineItem {
  phase: string;
  duration: string;
  activities: string[];
}

interface CaseStudy {
  id: number;
  company: string;
  industry: string;
  companySize: string;
  useCase: string;
  image: string;
  alt: string;
  challenge: string;
  solution: string;
  timeline: string;
  metrics: Metric[];
  executiveQuote: string;
  executiveName: string;
  executiveTitle: string;
  detailedChallenge: string;
  detailedSolution: string;
  implementationTimeline: TimelineItem[];
  riskMitigation: string[];
  keyTakeaways: string[];
}

interface CaseStudyModalProps {
  caseStudy: CaseStudy | null;
  onClose: () => void;
}

const CaseStudyModal = ({ caseStudy, onClose }: CaseStudyModalProps) => {
  useEffect(() => {
    if (caseStudy) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [caseStudy]);

  if (!caseStudy) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-display font-semibold text-foreground">
            Case Study: {caseStudy.company}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors duration-200"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6">
          <div className="relative h-64 rounded-lg overflow-hidden mb-6">
            <AppImage
              src={caseStudy.image}
              alt={caseStudy.alt}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4">
              <span className="inline-block px-3 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full">
                {caseStudy.industry}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="text-xs text-muted-foreground mb-1">Company Size</div>
              <div className="text-sm font-medium text-foreground">{caseStudy.companySize}</div>
            </div>
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="text-xs text-muted-foreground mb-1">Use Case</div>
              <div className="text-sm font-medium text-foreground">{caseStudy.useCase}</div>
            </div>
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="text-xs text-muted-foreground mb-1">Implementation Timeline</div>
              <div className="text-sm font-medium text-foreground">{caseStudy.timeline}</div>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-display font-semibold text-foreground mb-3">
              The Challenge
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {caseStudy.detailedChallenge}
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-display font-semibold text-foreground mb-3">
              Our Solution
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {caseStudy.detailedSolution}
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-display font-semibold text-foreground mb-4">
              Implementation Timeline
            </h3>
            <div className="space-y-4">
              {caseStudy.implementationTimeline.map((item, index) => (
                <div key={index} className="border-l-4 border-primary pl-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-foreground">{item.phase}</h4>
                    <span className="text-xs text-muted-foreground">{item.duration}</span>
                  </div>
                  <ul className="space-y-1">
                    {item.activities.map((activity, actIndex) => (
                      <li key={actIndex} className="flex items-start space-x-2 text-xs text-muted-foreground">
                        <Icon name="CheckIcon" size={12} className="text-success mt-0.5 flex-shrink-0" />
                        <span>{activity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-display font-semibold text-foreground mb-4">
              Measurable Results
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {caseStudy.metrics.map((metric, index) => (
                <div key={index} className="bg-muted/30 rounded-lg p-4">
                  <div className="text-2xl font-display font-semibold text-primary mb-1">
                    {metric.value}
                  </div>
                  <div className="text-sm text-foreground mb-2">
                    {metric.label}
                  </div>
                  <div className="flex items-center space-x-1 text-xs text-success">
                    <Icon name="ArrowTrendingUpIcon" size={12} />
                    <span>{metric.improvement}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-display font-semibold text-foreground mb-3">
              Risk Mitigation & Compliance
            </h3>
            <div className="space-y-2">
              {caseStudy.riskMitigation.map((item, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Icon name="ShieldCheckIcon" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-3 mb-3">
              <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-primary mt-1 flex-shrink-0" />
              <p className="text-sm text-foreground italic leading-relaxed">
                "{caseStudy.executiveQuote}"
              </p>
            </div>
            <div className="text-xs text-muted-foreground ml-8">
              — {caseStudy.executiveName}, {caseStudy.executiveTitle}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-display font-semibold text-foreground mb-3">
              Key Takeaways
            </h3>
            <div className="space-y-2">
              {caseStudy.keyTakeaways.map((takeaway, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Icon name="LightBulbIcon" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">{takeaway}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-secondary to-secondary/90 rounded-lg p-6 text-white text-center">
            <h4 className="text-lg font-display font-semibold mb-3">
              Ready to Achieve Similar Results?
            </h4>
            <p className="text-sm text-white/90 mb-4">
              Schedule a strategic consultation to discuss how we can help your organization achieve measurable AI transformation.
            </p>
            <a
              href="/partnership-inquiry"
              className="inline-flex items-center px-6 py-3 bg-accent text-accent-foreground font-display font-medium rounded-lg hover:shadow-authority transition-all duration-300 hover:-translate-y-0.5"
            >
              Request Consultation
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseStudyModal;